import _ from 'underscore';
import ko from 'knockout';
import { CronFormatter } from '@bingads-webui/cron-formatter';
import { fromLegacyTimeZone, toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';
import { dataDateRanges, frequencyGrains, weeklyOptions } from './options';

const setupOptions = (locKey, key, value, i18n) => ({
  Value: key,
  Text: i18n.getString(locKey + value),
});

const getDefaultTimeZone = accountTimeZone => (accountTimeZone > 0 ? accountTimeZone : 71);

const isHourlyNotifyMeRuleAllowed = (entity, permissions) => permissions.IsHourlyNotifyMeRuleAllowed === true && entity === 'Campaign';

class CustomDateSerializer {
  constructor({ date, hour, timeZone }) {
    this.year = date.getFullYear();
    this.month = date.getMonth() + 1;
    this.date = date.getDate();
    this.hour = hour;
    this.timeZone = timeZone;
  }

  toString() {
    return `${this.year}-${this.month}-${this.date} ${this.hour}`;
  }

  get format() {
    return 'yyyy-MM-dd H';
  }
}

function getUTCNowCustomDateSerializer() {
  const currentTime = new Date();
  const utcYear = currentTime.getUTCFullYear();
  const utcMonth = currentTime.getUTCMonth();
  const utcDate = currentTime.getUTCDate();
  const utcHours = currentTime.getUTCHours();

  currentTime.setFullYear(utcYear);
  currentTime.setMonth(utcMonth);
  currentTime.setDate(utcDate);

  return new CustomDateSerializer({
    date: currentTime,
    hour: utcHours,
    timeZone: 'UTC',
  });
}

export class RuleCronSelectionviewModel {
  constructor({
    i18n,
    permissions,
    accountTimeZone,
    selectedRule,
    stateMgr,
    preferenceSvc,
    appCfg,
    entity,
    isEmailOption,
    instrumentation,
  }) {
    const {
      frequencyGrain,
      dateRangePreset,
      monthlyOption,
      weeklyOption,
      dailyOption,
      timeZoneOption,
      runOnceDate,
    } = selectedRule || {};

    const scenario = instrumentation[`authorRule${entity}`].create();
    this.activity = scenario.createRule.create();

    this.cronFormatter = new CronFormatter({ i18n });
    this.i18n = i18n;
    this.frequencyGrains = _.map(
      frequencyGrains,
      (key, value) => setupOptions('FrequencyGrain_', key, value, i18n)
    );
    this.dataDateRangeOptions = _.map(
      dataDateRanges,
      (key, value) => setupOptions('DataDateRangeOptions_', key, value, i18n)
    );
    this.monthlyOptions = _.map(_.range(1, 29), i => ({ Value: i, Text: i }));
    this.weeklyOptions = _.map(
      weeklyOptions,
      (key, value) => setupOptions('WeeklyOptions_', key, value, i18n)
    );
    this.dailyOptions = _.map(_.range(0, 24), hour => ({
      Value: hour,
      twelveHourDisplay: this.cronFormatter.formatHourOfDay(hour),
      twentyFourHourDisplay: `0${hour}`.slice(-2),
    }));

    this.isTwelveHourDisplay = ko.observable('true');
    if (permissions.IsUsePreferencesServiceEnabled === true) {
      preferenceSvc.initialize().then(() => {
        const prefix = appCfg.get('StateManagerKeyPrefix');
        const isTwelveHourDisplayPreference = preferenceSvc.findByName(`${prefix}isTwelveHourDisplay`);

        if (isTwelveHourDisplayPreference) {
          // unless it is set to 'false', by default it's 'true'
          const result = isTwelveHourDisplayPreference.Data === 'false' ? 'false' : 'true';
          this.isTwelveHourDisplay(result);
        }
      });
    } else {
      stateMgr.getItem('isTwelveHourDisplay', (err, state) => {
        if (!err && state) {
          this.isTwelveHourDisplay(_.isUndefined(state.permanent) ? 'true' : state.permanent);
        }
      });
    }
    this.timeZoneOptions = _.map(_.range(75, 0, -1), zone => ({ Value: zone, Text: i18n.getString(`TimeZoneId_${zone}`) }));
    this.selectedDataDateRangeOption = ko.observable(dateRangePreset);
    this.selectedMonthOption = ko.observable(monthlyOption);
    this.selectedWeekOption = ko.observable(weeklyOption);
    this.selectedDailyOption = ko.observable(dailyOption);
    this.selectedTimeZoneOption = ko.observable(timeZoneOption > 0 ?
      timeZoneOption :
      getDefaultTimeZone(accountTimeZone));
    this.selectedFrequencyGrain = ko.observable(frequencyGrain);
    this.selectedFrequencyGrain.subscribe((newValue) => {
      switch (newValue) {
        case frequencyGrains.Monthly:
          this.selectedDataDateRangeOption(dataDateRanges.Last30Days);
          break;
        case frequencyGrains.Weekly:
          this.selectedDataDateRangeOption(dataDateRanges.Last7Days);
          break;
        case frequencyGrains.Once:
          this.selectedDataDateRangeOption(dataDateRanges.Today);
          break;
        default:
          this.selectedDataDateRangeOption(dataDateRanges.Yesterday);
      }
    });

    this.isTwelveHourDisplayFormat = ko.computed({
      read() {
        return this.isTwelveHourDisplay();
      },
      write(v) {
        this.isTwelveHourDisplay(v);
        if (permissions.IsUsePreferencesServiceEnabled === true) {
          const prefix = appCfg.get('StateManagerKeyPrefix');
          const data = { Name: `${prefix}isTwelveHourDisplay`, Data: v };
          preferenceSvc.setAtUserLevel(data);
        } else {
          stateMgr.setItem('permanent', 'isTwelveHourDisplay', v);
        }
      },
    }, this);

    this.runOnceDate = ko.observable(runOnceDate);
    this.isMonthOption = ko.computed(() =>
      this.selectedFrequencyGrain() === frequencyGrains.Monthly, this, {
      pure: true,
    });
    this.isWeekOption = ko.computed(() =>
      this.selectedFrequencyGrain() === frequencyGrains.Weekly, this, {
      pure: true,
    });
    this.isOnceOption = ko.computed(() =>
      this.selectedFrequencyGrain() === frequencyGrains.Once, this, {
      pure: true,
    });
    this.isHourlyOption = ko.computed(() =>
      this.selectedFrequencyGrain() === frequencyGrains.Hourly, this, {
      pure: true,
    });
    this.frequencyErrorMessage = ko.observable('');
    this.selectedFrequencyGrain.subscribe(() => {
      this.frequencyErrorMessage('');
    });
    this.frequencyErrorRole = ko.computed(() => (this.frequencyErrorMessage() === '' ? 'none' : 'alert'), this);
    this.startInput = ko.computed({
      read: () => (this.runOnceDate() ? i18n.formatDate(this.runOnceDate(), { timeZone: null }) : ''),
      write: (value) => {
        this.frequencyErrorMessage('');

        if (!_.isNull(value) &&
          !_.isEmpty(value.trim()) &&
          !_.isNull(i18n.parseDate(value.trim(), { timeZone: null }))) {
          this.runOnceDate(i18n.parseDate(value.trim(), { timeZone: null }));
        } else {
          this.frequencyErrorMessage(i18n.getString('Validate_ScheduleTimeInvalid'));
        }
      },
    }, this, {
      pure: true,
    });

    // add Hourly notify-me rule for users under pilot
    if (!isHourlyNotifyMeRuleAllowed(entity, permissions) || isEmailOption) {
      this.frequencyGrains = _.reject(
        this.frequencyGrains,
        item => item.Value === frequencyGrains.Hourly
      );
    }
  }

  validate() {
    const date = this.runOnceDate();

    if (date && this.isOnceOption()) {
      const selectedTimeZone = toIANATimeZone(fromLegacyTimeZone(this.selectedTimeZoneOption()));
      const selectedTime = new CustomDateSerializer({
        date,
        hour: this.selectedDailyOption(),
        timeZone: selectedTimeZone,
      });
      const convertedSelectedTime = this.i18n.parseDate(
        selectedTime.toString(),
        {
          timeZone: selectedTime.timeZone,
          raw: selectedTime.format,
        }
      ); // convert to account timezone

      const currentTime = getUTCNowCustomDateSerializer();
      const convertedCurrentTime = this.i18n.parseDate(
        currentTime.toString(),
        {
          raw: currentTime.format,
          timeZone: currentTime.timeZone,
        }
      ); // convert now to account timezone

      this.activity.trace(`[TestingClientSideValidation] Validating selected run once time. Selected time zone: ${selectedTimeZone}. Selected time: ${selectedTime.toString()}. Converted rule run time: ${convertedSelectedTime.toString()}. Converted current time: ${convertedCurrentTime.toString()}. Is run time in future? ${convertedCurrentTime < convertedSelectedTime}`);
    }
    return true;
  }

  getCron() {
    let cron = '* 23 * * *'; // some default value until we integrate run once.
    switch (this.selectedFrequencyGrain()) {
      case frequencyGrains.Hourly:
        cron = this.cronFormatter.toCronString({
          min: 0,
        });
        break;
      case frequencyGrains.Weekly:
        cron = this.cronFormatter.toCronString({
          hour: this.selectedDailyOption(),
          dayOfWeek: this.selectedWeekOption(),
        });
        break;
      case frequencyGrains.Monthly:
        cron = this.cronFormatter.toCronString({
          hour: this.selectedDailyOption(),
          dayOfMonth: this.selectedMonthOption(),
        });
        break;
      case frequencyGrains.Once:
        cron = this.cronFormatter.toCronString({
          hour: this.selectedDailyOption(),
          dayOfMonth: this.runOnceDate().getDate(),
          month: this.runOnceDate().getMonth() + 1,
          year: this.runOnceDate().getFullYear(),
        });
        break;
      case frequencyGrains.Daily:
      default:
        cron = this.cronFormatter.toCronString({
          hour: this.selectedDailyOption(),
        });
        break;
    }

    return cron;
  }
}
